import { Modal, Form, Input, Select, DatePicker, Alert, App } from 'antd'
import { useState, useEffect, useMemo } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { submitTerminationRequest, updateTerminationRequest } from '../services/termination-request-api'
import { lookupTenants } from '../../tenants/services/tenant-api'
import type { Tenant } from '../../tenants/types/tenant.types'
import { formatPermanentDeletionDate } from '../utils/permanent-deletion'
import type { TerminationRequest } from '../types/termination-request.types'

interface TerminateWorkspaceModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  editRequest?: TerminationRequest | null
}

// Workspace lookup stays empty until the user has typed enough to narrow it down
const MIN_WORKSPACE_SEARCH_LENGTH = 3

interface TerminateWorkspaceFormValues {
  tenantCode: string
  inactiveDate: Dayjs | null
  reason: string
  notes?: string
}

export function TerminateWorkspaceModal({ open, onClose, onSuccess, editRequest }: TerminateWorkspaceModalProps) {
  const { message } = App.useApp()
  const [form] = Form.useForm<TerminateWorkspaceFormValues>()
  const [loading, setLoading] = useState(false)
  const [workspaces, setWorkspaces] = useState<Tenant[]>([])
  const [workspaceSearch, setWorkspaceSearch] = useState('')

  const isEditMode = !!editRequest
  const selectedTenantCode = Form.useWatch('tenantCode', form)

  const workspaceOptions = useMemo(
    () => workspaces.map(w => ({ label: `${w.tenantName} (${w.workspaceCode})`, value: w.tenantCode })),
    [workspaces]
  )

  // Below the minimum search length keep only the selected option, so the field still renders its label
  const visibleWorkspaceOptions = useMemo(() => {
    const search = workspaceSearch.trim().toLowerCase()
    if (search.length < MIN_WORKSPACE_SEARCH_LENGTH) {
      return workspaceOptions.filter(o => o.value === selectedTenantCode)
    }
    return workspaceOptions.filter(o => o.label.toLowerCase().includes(search))
  }, [workspaceOptions, workspaceSearch, selectedTenantCode])

  // Fetch active workspaces for the searchable lookup
  useEffect(() => {
    if (open) {
      lookupTenants()
        .then(response => setWorkspaces(response.data))
        .catch(err => {
          console.error('Failed to fetch workspaces:', err)
          setWorkspaces([])
        })
    }
  }, [open])

  // Populate form when editing
  useEffect(() => {
    if (open) {
      if (editRequest) {
        form.setFieldsValue({
          tenantCode: editRequest.tenantCode,
          inactiveDate: editRequest.inactiveDate ? dayjs(editRequest.inactiveDate) : null,
          reason: editRequest.reason,
          notes: editRequest.notes || undefined
        })
      }
    }
  }, [open, editRequest, form])

  const handleSubmit = async (values: TerminateWorkspaceFormValues) => {
    try {
      setLoading(true)

      const requestData = {
        tenantCode: values.tenantCode,
        inactiveDate: values.inactiveDate!.format('YYYY-MM-DD'),
        reason: values.reason,
        notes: values.notes || undefined
      }

      if (isEditMode && editRequest) {
        await updateTerminationRequest(editRequest.id, requestData)
        message.success('Termination request updated successfully')
      } else {
        await submitTerminationRequest(requestData)
        message.success('Termination request submitted successfully')
      }

      form.resetFields()
      setWorkspaceSearch('')
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'submit'} termination request:`, error)

      const responseData = error.response?.data
      if (responseData?.errors && typeof responseData.errors === 'object') {
        const fieldErrors = Object.entries(responseData.errors).map(([field, messages]) => ({
          name: field as keyof TerminateWorkspaceFormValues,
          errors: Array.isArray(messages) ? messages : [messages]
        }))
        form.setFields(fieldErrors)
      }
      const errorMessage =
        responseData?.error?.message ||
        responseData?.ErrorMessage ||
        responseData?.message ||
        `Failed to ${isEditMode ? 'update' : 'submit'} termination request`
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={isEditMode ? 'Edit termination request' : 'Terminate workspace'}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Submit request"
    >
      <Alert
        type="warning"
        showIcon
        message="On approval the workspace becomes inactive on the date below. Workspace data is not deleted."
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Workspace"
          name="tenantCode"
          rules={[{ required: true, message: 'Please select a workspace' }]}
        >
          <Select
            showSearch
            placeholder={`Type at least ${MIN_WORKSPACE_SEARCH_LENGTH} characters to search`}
            options={visibleWorkspaceOptions}
            filterOption={false}
            defaultActiveFirstOption={false}
            onSearch={setWorkspaceSearch}
            onDropdownVisibleChange={open => {
              if (!open) setWorkspaceSearch('')
            }}
            notFoundContent={
              workspaceSearch.trim().length < MIN_WORKSPACE_SEARCH_LENGTH
                ? `Type at least ${MIN_WORKSPACE_SEARCH_LENGTH} characters to search workspaces`
                : 'No matching workspace'
            }
            disabled={isEditMode}
          />
        </Form.Item>

        <Form.Item
          label="Inactive date"
          name="inactiveDate"
          rules={[{ required: true, message: 'Please select the inactive date' }]}
          extra="Usually one day after the subscription end date."
        >
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.inactiveDate !== cur.inactiveDate}>
          {({ getFieldValue }) => (
            <Form.Item
              label="Permanent deletion date"
              extra="Retention tracking only — no data is deleted automatically."
            >
              <Input disabled value={formatPermanentDeletionDate(getFieldValue('inactiveDate'))} />
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item
          label="Reason"
          name="reason"
          rules={[
            { required: true, message: 'Please enter a reason' },
            { max: 1000, message: 'Reason must be 1000 characters or less' }
          ]}
        >
          <Input.TextArea rows={3} maxLength={1000} placeholder="Reason for terminating this workspace" />
        </Form.Item>

        <Form.Item
          label="Notes"
          name="notes"
          rules={[{ max: 1000, message: 'Notes must be 1000 characters or less' }]}
          extra="Optional notes for this request"
        >
          <Input.TextArea rows={2} placeholder="Add any notes or comments about this request" maxLength={1000} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
